import { Box, HStack, Link, Tag, Text } from "@chakra-ui/react";
import { EVENT_MAPPINGS } from "../../../constants/firebase";
import { trackEvent } from "../../../utils/firebase";
import { isExternalLink } from "../../../utils/common";
import useOriginURL from "../../../hooks/useOriginURL";
import NextLink from "next/link";
import {
  DISCONTINUED_BADGE_BG,
  DISCONTINUED_BADGE_COLOR,
  getDiscontinued,
  resolveDiscontinuedLink,
} from "../../../constants/discontinued";

type Props = {
  label: string;
  items: Array<{
    label: string;
    detailId: string;
    link: string;
    tag?: string;
    dataKey?: string;
  }>;
};

const Column = ({ label: title, items }: Props) => {
  const originURL = useOriginURL();

  const isSPALink = (link: string | null) => {
    if (isExternalLink(link)) {
      return false;
    } else if (originURL) {
      return false;
    }
    return true;
  };

  const getLink = (link: string | null) => {
    if (link && isExternalLink(link)) {
      return link;
    }
    if (link) {
      return originURL + link;
    }

    return "";
  };

  return (
    <Box textAlign="left" w={244}>
      <Text
        fontSize={{ base: 20, lg: 22.5 }}
        fontWeight={{ base: 600, lg: 800 }}
        mb={{ base: 3, lg: 4 }}
      >
        {title}
      </Text>
      {items.map(({ detailId, label, link, tag, dataKey }, index: number) => {
        const discontinued = getDiscontinued(dataKey);
        const badge = tag ?? discontinued?.badge;
        // A CMS tag ("NEW") is a promo and keeps the accent colour. "Discontinued"
        // is the opposite signal, so it stays muted against the dark footer.
        const isMutedBadge = !tag && !!discontinued;
        // Sunset networks keep their row, but the dApp they used to link to is
        // gone — send them to the Learn more page instead.
        const href = resolveDiscontinuedLink(dataKey, link);
        return (
          <HStack
            key={`${detailId}-${index}`}
            align="center"
            gap={3}
            opacity={0.7}
          >
            <Link
              as={isSPALink(href) ? NextLink : Link}
              href={getLink(href)}
              rel={title === "Networks" ? "canonical" : "noopener noreferrer"}
              target={isExternalLink(href) ? "_blank" : "_self"}
              legacyBehavior={false}
            >
              <Text
                cursor="pointer"
                color="#FFFFFF"
                fontWeight="400"
                fontSize={{ base: 12, lg: 16 }}
                py={1}
                display="block"
                onClick={() => {
                  trackEvent(EVENT_MAPPINGS[title.toLowerCase()] || title, {
                    cta: "footer",
                    name: label,
                  });
                }}
              >
                {label}
              </Text>
            </Link>
            {badge && (
              <Tag
                className="tag"
                bg={isMutedBadge ? DISCONTINUED_BADGE_BG : "notification"}
                minH="auto"
                py="3px"
                px="8px"
                borderRadius="full"
                textTransform="uppercase"
                color={isMutedBadge ? DISCONTINUED_BADGE_COLOR : "white"}
              >
                <Text
                  fontSize="9px"
                  fontWeight={700}
                  letterSpacing="0.6px"
                  lineHeight="1"
                >
                  {badge}
                </Text>
              </Tag>
            )}
          </HStack>
        );
      })}
    </Box>
  );
};

export default Column;
